'use client';

import OneSchemaImporter from '@oneschema/react';
import { FloatingPortal } from '@floating-ui/react';
import type { ClientsImportDialogProps } from './ClientsImportDialog.types';
import { UserContext } from '@/_contexts/UserProvider';
import { useEffect, useState, useCallback, useContext, type FC } from 'react';
import { isSorabanEmail, isSorabanAdmin } from '@/_utils/admin';
import {
  Button,
  ButtonGroup,
  Card,
  File,
  Flex,
  Heading,
  Icon,
  Input,
  Dialog,
  Alert,
  UploadBox,
  Select,
  SelectItem,
} from '@/_components';
import { ToastContext } from '@/_contexts/ToastProvider';
import { TAX_YEARS, CURRENT_TAX_YEAR } from '@/_constants/year-options';
import { IMPORT_METHODS, CLIENT_LIST_TYPES } from '@/_constants/imports';
import { PRIOR_YEAR_IMPORT_METHODS } from '@/_constants/prior-year';
import {
  getBusinessImportTemplateLink,
  getIndividualImportTemplateLink,
  uploadClientsList,
  uploadDrakeBackup,
} from '@/_api/admin-clients';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { ClientsImportDIYDialog } from '../ClientsImportDIYDialog/ClientsImportDIYDialog';
import { ImportOptionsSection } from './_components/ImportOptionsSection';

const IMPORT_METHOD_ITEMS: Array<SelectItem> = IMPORT_METHODS.map((m) => ({
  name: m.name,
  value: m.value,
}));

const CLIENT_LIST_TYPES_ITEMS: Array<SelectItem> = CLIENT_LIST_TYPES.map(
  (m) => ({
    name: m.name,
    value: m.value,
  }),
);

export const ClientsImportDialog: FC<ClientsImportDialogProps> = ({
  isOpen,
  onToggleDialog,
}) => {
  const { addToast, resetToasts } = useContext(ToastContext);
  const { token, user } = useContext(UserContext);
  const { firm, firmUsers, preferences } = useContext(FirmSettingsContext);

  const [taxYear, setTaxYear] = useState<string>(CURRENT_TAX_YEAR);
  const [method, setMethod] = useState<string>('');
  const [taxSoftware, setTaxSoftware] = useState<string | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploaded, setUploaded] = useState<boolean>(false);
  const [firmUserAssigneeId, setFirmUserAssigneeId] = useState<any>('');
  const [fileDescription, setFileDescription] = useState<string>('');
  const [filesToBeUploaded, setFilesToBeUploaded] = useState<any>([]);
  const [clientListType, setClientListTyped] = useState<string>('');
  const [showDIYImport, setShowDIYImport] = useState<boolean>(false);

  const getIsSorabanEmail: boolean = isSorabanEmail(
    user?.email,
    user?.is_advisor,
  );

  const getIsSorabanAdmin: boolean = isSorabanAdmin(
    user?.email,
    user?.is_advisor,
  );

  const fileTypes: Array<SelectItem> = [
    ...(!method
      ? [{ name: 'Select from supported file types', value: '' }]
      : []), // Only show placeholder if no methods are selected
    ...(getIsSorabanEmail
      ? IMPORT_METHOD_ITEMS
      : IMPORT_METHOD_ITEMS.filter((m) => m.value !== 'rollover_items_import')),
    // Only show Rollover Items CSV for MGA
    ...(firm?.id === 633
      ? [{ name: 'Rollover Items CSV', value: 'rollover_items_csv' }]
      : []),
  ];

  const onDownloadIndividualTemplate = async () => {
    try {
      const { document } = (await getIndividualImportTemplateLink({
        token,
      })) as any;

      globalThis.open(document?.data?.link, '_blank');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const onDownloadBusinessTemplate = async () => {
    try {
      const { document } = (await getBusinessImportTemplateLink({
        token,
      })) as any;

      globalThis.open(document?.data?.link, '_blank');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  useEffect(() => {
    !isOpen && resetToasts();
    !isOpen && setUploaded(false);
    !isOpen && setFilesToBeUploaded([]);
  }, [isOpen, resetToasts]);

  const onDrop = useCallback((acceptedFiles: Array<File>) => {
    for (const file of acceptedFiles) {
      if (file?.size === 0) continue;

      setFilesToBeUploaded((files: any) => [...files, file]);
    }

    // Remove duplicately named files
    setFilesToBeUploaded((files: any) => {
      const fileNames = files.map((file: any) => file.name);

      return files.filter(
        (file: any, index: any) => fileNames.indexOf(file.name) === index,
      );
    });
  }, []);

  const onUpload = async () => {
    try {
      setLoading(true);

      for (const file of filesToBeUploaded) {
        if (
          method === 'rollover_items_import' &&
          taxSoftware === 'drake_backup'
        ) {
          setUploadProgress(0);
          try {
            await uploadDrakeBackup({
              file,
              documentName: file.name,
              token,
              onProgress: (uploaded, total) => {
                setUploadProgress(Math.floor((uploaded / total) * 100));
              },
            });
          } catch {
            setLoading(false);
            setUploadProgress(0);
            return addToast(
              'error',
              'Drake backup import type is disabled for this firm.',
            );
          }
        } else {
          await uploadClientsList({
            file,
            assignedFirmUserId: method === 'client_list' && firmUserAssigneeId,
            comment: fileDescription,
            documentName: file.name,
            documentType: method,
            taxSoftware,
            year: taxYear,
            token,
          });
        }
      }

      setUploaded(true);
      setFilesToBeUploaded([]);
    } catch (error) {
      return addToast('error', 'File upload failed, please try again.', {
        error,
      });
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const onYearChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setTaxYear(event.target.value);
  const onMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setMethod(event.target.value);

  const variousAssigneeItem = {
    name: 'Various (add firm user names as a column in client list)',
    value: '0',
  };
  const nonAssigneeItem = {
    name: 'Select Assignee',
    value: '',
  };

  const assigneeItems = [
    nonAssigneeItem,
    variousAssigneeItem,
    ...(firmUsers || []).map((u: any) => ({
      name: u.name,
      value: u.id,
    })),
  ];

  const disableUpload =
    !filesToBeUploaded.length ||
    isLoading ||
    !method ||
    (method === 'client_list' && !firmUserAssigneeId) ||
    (method === 'client_list' && !clientListType) ||
    (method === 'zipped_rollover_items' && !taxSoftware) ||
    (method === 'other_document' && !fileDescription) ||
    (method === 'rollover_items_import' && !taxYear) ||
    (method === 'rollover_items_import' && !taxSoftware) ||
    (method === 'rollover_items_csv' && !taxYear);

  const [isOneSchemaIndividualsOpen, setIsOneSchemaIndividualsOpen] =
    useState(false);

  const onClose = () => {
    onToggleDialog();
    setMethod('');
    setShowDIYImport(false);
  };

  const onSelectPath = () => {
    setShowDIYImport(true);
  };

  if (showDIYImport) {
    return (
      <ClientsImportDIYDialog
        isOpen={isOpen}
        onClose={onClose}
        onBack={() => setShowDIYImport(false)}
      />
    );
  }

  return (
    <Dialog isOpen={isOpen} title="Import" onClose={onClose} isWide={true}>
      <Dialog.Content>
        {getIsSorabanAdmin && (
          <Flex padding={8} gap={4} isColumn>
            <Card className="p-4">
              <Flex gap={4} isColumn>
                <Alert type="danger">
                  <h4>(This box is only visible by Soraban admins)</h4>
                </Alert>
                <button onClick={() => setIsOneSchemaIndividualsOpen(true)}>
                  Split names with OneSchema
                </button>
              </Flex>
            </Card>
          </Flex>
        )}

        <FloatingPortal>
          <OneSchemaImporter
            isOpen={isOneSchemaIndividualsOpen}
            onRequestClose={() => setIsOneSchemaIndividualsOpen(false)}
            clientId="17d7b76c-4640-4834-b4f9-c9cf49bfb413"
            userJwt="eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiIxN2Q3Yjc2Yy00NjQwLTQ4MzQtYjRmOS1jOWNmNDliZmI0MTMiLCJ1c2VyX2lkIjoiPFVTRVJfSUQ-In0.D1FjRdc35Ps8KcdgzVzpnJebgj7pBL2B4eS48-kTtUg"
            templateKey="individuals_template"
            importConfig={{ type: 'local' }}
            devMode={!globalThis?.location?.href?.includes('app.soraban.com')}
            className="oneschema-importer"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '95vh',
              zIndex: 1000,
            }}
          />
        </FloatingPortal>

        <Flex isColumn={true} gap={8}>
          <Flex gap={4} yAlign="end">
            <Select
              isFullWidth={!method}
              label="File Type"
              isLabelHidden={!method}
              placeholder="Select from supported file types"
              items={fileTypes}
              selectedValue={method}
              onChange={onMethodChange}
            />

            {/* Only showing the year selection if it's a Soraban admin because this is only necessary for Rollover Items Import which is a thing only Soraban admins will do */}
            {((getIsSorabanEmail && method === 'rollover_items_import') ||
              method === 'rollover_items_csv') && (
              <Select
                label="Year"
                items={TAX_YEARS}
                placeholder="Select Tax Year"
                selectedValue={taxYear}
                onChange={onYearChange}
              />
            )}

            {method === 'client_list' && (
              <Select
                label="Client List Type"
                placeholder="Select Type"
                items={CLIENT_LIST_TYPES_ITEMS}
                selectedValue={clientListType}
                onChange={(e) => setClientListTyped(e.target.value)}
              />
            )}

            {(method === 'zipped_rollover_items' ||
              method === 'rollover_items_import') && (
              <Select
                label="Tax Software"
                items={PRIOR_YEAR_IMPORT_METHODS.filter((s) => s.bulk_supported)
                  .filter((s) =>
                    getIsSorabanEmail ? true : s.value !== 'drake_backup',
                  )
                  .map((s) => ({
                    name: s.name,
                    value: s.value,
                  }))}
                selectedValue={taxSoftware}
                placeholder="Select Tax Software"
                onChange={(e) => setTaxSoftware(e.target.value)}
              />
            )}

            {method === 'other_document' && (
              <Input
                type="text"
                label="File description"
                placeholder="Please provide a file description"
                value={fileDescription}
                onChange={(e) => setFileDescription(e.target.value)}
              />
            )}
          </Flex>

          {!isUploaded && method === 'client_list' && (
            <>
              {preferences?.client_list_import_diy_enabled &&
                clientListType === 'individuals' && (
                  <ImportOptionsSection onSelectPath={onSelectPath} />
                )}

              <Alert type="info">
                Please download the{' '}
                <b>
                  <u>
                    <a onClick={onDownloadIndividualTemplate}>
                      template for Individuals
                    </a>
                  </u>
                </b>{' '}
                or the{' '}
                <b>
                  <u>
                    <a onClick={onDownloadBusinessTemplate}>
                      template for Businesses
                    </a>
                  </u>
                </b>{' '}
                (depending which list type you are uploading). Fill in the
                information of clients you want to import and upload it here or
                upload the client list generated by your tax software. All files
                uploaded are encrypted and secure. Client List takes about 7-10
                business days to process.
              </Alert>
            </>
          )}

          {method === 'zipped_rollover_items' && (
            <Alert type="info">
              Organizers and backup files <strong>MUST</strong> be uploaded in a
              compressed zip folder. It takes about 3-5 business days to process
              the rollover items, but may take longer depending on the number of
              clients.
            </Alert>
          )}

          {method === 'rollover_items_csv' && (
            <Alert type="info">
              Rollover Items CSV is a CSV file that contains the rollover items
              for the year. Column header is not needed. Below is the column
              order:
              <br />
              <br />
              <ol>
                <li>Client Name (not used)</li>
                <li>External Entity ID</li>
                <li>Entity Type (not used)</li>
                <li>Rollover Item Form Name</li>
                <li>Rollover Item Detailed Name</li>
                <li>Due Date (not used)</li>
                <li>Rollover to Next Year (Y/N)</li>
              </ol>
            </Alert>
          )}

          {isUploaded && method === 'zipped_rollover_items' ? (
            <Alert type="success">
              Successfully uploaded. We will send you a notification once it has
              been processed!
            </Alert>
          ) : isUploaded && method === 'client_list' ? (
            <Alert type="success">
              Successfully uploaded. We will send you a notification once it has
              been processed!
            </Alert>
          ) : (
            isUploaded && <Alert type="success">Successfully uploaded.</Alert>
          )}

          {method === 'client_list' && (
            <Select
              label="Assign to"
              placeholder="Select Assignee"
              items={assigneeItems}
              selectedValue={firmUserAssigneeId}
              onChange={(e) => setFirmUserAssigneeId(e.target.value)}
              isFullWidth
            />
          )}

          {method && (
            <UploadBox
              onDrop={onDrop}
              isLoading={isLoading}
              allowMultiple={true}
              maxSizeText={
                taxSoftware === 'drake_backup' && uploadProgress >= 1
                  ? `Uploading... ${uploadProgress}%`
                  : undefined
              }
            />
          )}

          {filesToBeUploaded.length > 0 && (
            <>
              <Heading level={4}>File(s) to be uploaded</Heading>

              {filesToBeUploaded.map((file: any, index: any) => (
                <File key={index} title={file.name}>
                  <Icon
                    onClick={() => {
                      setFilesToBeUploaded((files: any) =>
                        files.filter((f: any) => f.name !== file.name),
                      );
                    }}
                    size={16}
                    name="trash"
                    color="critical"
                  />
                </File>
              ))}

              <ButtonGroup align="right">
                <Button
                  intent="primary"
                  onClick={onUpload}
                  value="Upload"
                  isDisabled={disableUpload}
                />
              </ButtonGroup>
            </>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
